/**
 * Build identity + server-version probe.
 *
 * Pairs with the Vite `versionJsonPlugin` in vite.config.ts. The plugin
 * embeds `__BUILD_ID__` into the client bundle AND emits a matching
 * `/version.json` at the deploy root. The client uses this pair to
 * detect when a newer deploy has landed and the current tab is running
 * stale JS — then `VersionUpdateBanner` prompts the user to reload.
 *
 * Why not a service worker: tracked in CLAUDE.md gotcha #51. Short
 * version: SW caching has well-documented failure modes (Linear's prod
 * incident) and we don't need offline support, so this lighter pattern
 * is sufficient.
 */

/** The build hash this bundle was compiled from. */
export const BUILD_ID: string =
  typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "unknown";

/** ISO timestamp of when this bundle was built. */
export const BUILD_TIME: string =
  typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : new Date().toISOString();

export interface VersionManifest {
  buildId: string;
  buildTime: string;
}

/**
 * Fetch the deployed `/version.json`. A cache-buster query param makes
 * sure no intermediate cache (browser, CDN) returns a stale value —
 * Firebase Hosting's default cache headers on JSON aren't sufficient
 * on their own. Returns null on any failure (offline, 404, parse error)
 * so the caller can no-op gracefully.
 */
export async function fetchServerVersion(): Promise<VersionManifest | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as unknown;
    if (
      !data ||
      typeof data !== "object" ||
      typeof (data as VersionManifest).buildId !== "string" ||
      typeof (data as VersionManifest).buildTime !== "string"
    ) {
      return null;
    }
    return data as VersionManifest;
  } catch {
    return null;
  }
}

/** True if the deployed server build is different from this bundle. */
export function isStaleBuild(
  serverBuildId: string,
  currentBuildId: string = BUILD_ID
): boolean {
  if (!serverBuildId || !currentBuildId) return false;
  if (currentBuildId === "unknown") return false; // Dev mode, no comparison possible
  return serverBuildId !== currentBuildId;
}
